import { FC, Fragment } from 'react'

import Badge from 'src/components/ui/atoms/Badge'
import Separator from 'src/components/ui/atoms/Separator'
import SidebarMenuItem from 'src/components/ui/atoms/SidebarMenuItem'
import Typography from 'src/components/ui/atoms/Typography'
import useLoadDashboard from 'src/hooks/useLoadDashboard'
import useProductMatchParams from 'src/hooks/useProductMatchParams'
import useSelectedDashboardStore from 'src/stores/useSelectedDashboardStore'
import { AppMenuCountryInterface, AppMenuSubProductInterface } from 'src/types/AppMenuInterface'
import { isTrueOrUndefinedFn } from 'src/utils/booleanUtils'
import { sortDashboardsFn } from 'src/utils/sortDashboardUtils'
import { getLabelWithPluralFn } from 'src/utils/textUtils'
import SidebarProductCountries from './SidebarProductCountries'
import SidebarProductCustomDashboards from './SIdebarProductCustomDashboards'
import SidebarProductDashboard from './SidebarProductDashboard'

interface SidebarProductSubProductProps {
  subProduct: AppMenuSubProductInterface
  productCode: string
  isVisible: boolean
}

const SidebarProductSubProduct: FC<SidebarProductSubProductProps> = ({
  subProduct,
  productCode,
  isVisible,
}) => {
  const {
    name,
    code: subProductCode,
    dashboards: subProductDashboards,
    has_access,
    program_list: programList,
    alert_count: alert,
    active_program: activeProgram,
    custom_dashboard_label: customDashboardLabel,
  } = subProduct
  const hasAccess = isTrueOrUndefinedFn(has_access)

  const routeMatchParam = useProductMatchParams()
  const { setSelectedDashboard, selectedDashboard } = useSelectedDashboardStore()
  const { handleGetDashboard } = useLoadDashboard(subProductCode)

  const subProductPath = `/${productCode}/${subProductCode}`

  const alertCount = alert ? (typeof alert === 'number' ? alert : alert[activeProgram as any]) : 0

  // Reset the selected dashboard when clicking on a sub product
  const handleClickSubProduct = () => {
    if (hasAccess) {
      setSelectedDashboard(null)
      handleGetDashboard({ id: null })
    }
  }

  const isSubProductActive =
    !selectedDashboard && routeMatchParam?.params?.subProduct === subProductCode

  const dashboards = hasAccess ? sortDashboardsFn(subProductDashboards || []) : []

  const savedDashboards = dashboards.filter(dashboard => dashboard.default_dashboard)

  const customDashboards = dashboards.filter(dashboard => !dashboard.default_dashboard)

  const favoriteDashboards = dashboards.filter(dashboard => dashboard.favorite)

  const countryDashboards = (programList || []).sort((a, b) =>
    Intl.Collator('en', { sensitivity: 'base' }).compare(a.label, b.label),
  )

  const mappedCountry = new Map<string, AppMenuCountryInterface>(
    countryDashboards.map(country => [country.id.toString(), country]),
  )

  return (
    <>
      {isVisible && subProduct.section && (
        <Typography
          className="text-hit-gray py-1 pl-12 font-normal"
          variant="sectionSeparatorTitle"
        >
          {subProduct.section}
        </Typography>
      )}
      {/* Favorite dashboard is displayed first */}
      {favoriteDashboards.map(dashboard => {
        const sectionTitle = dashboard.program_id
          ? mappedCountry.get(dashboard.program_id.toString())?.label
          : undefined

        return (
          <Fragment key={dashboard.id}>
            {sectionTitle && isVisible && (
              <Typography
                className="text-hit-gray py-1 pl-12 font-normal"
                variant="sectionSeparatorTitle"
              >
                {sectionTitle}
              </Typography>
            )}
            <SidebarProductDashboard
              dashboard={dashboard}
              subProductCode={subProductCode}
              link={`${subProductPath}${dashboard.program_id ? `/${dashboard.program_id}` : ''}`}
              isVisible={isVisible}
              hasAccess={isTrueOrUndefinedFn(dashboard.has_access)}
              programId={dashboard.program_id?.toString()}
              className="pl-12"
              showActiveIndicator={false}
            />
            {sectionTitle && isVisible && (
              <Separator className="bg-loblolly my-2 ml-12 h-px w-[calc(100%-var(--spacing)*16)]" />
            )}
          </Fragment>
        )
      })}
      {!countryDashboards.length && (
        <SidebarMenuItem
          onClick={handleClickSubProduct}
          label={
            <div className="flex items-center gap-2">
              <span>{name}</span>
              {Boolean(alertCount) && (
                <Badge
                  data-testid="sidebar-menu-item-notification-badge"
                  type="notificationMenu"
                  label={alertCount}
                />
              )}
            </div>
          }
          active={isSubProductActive}
          to={hasAccess ? subProductPath : undefined}
          className="pl-12"
          isVisible={isVisible}
          addon={!hasAccess ? `${subProductCode}-addon` : undefined}
        />
      )}
      {savedDashboards.map(dashboard => (
        <SidebarProductDashboard
          subProductCode={subProductCode}
          key={dashboard.id}
          dashboard={dashboard}
          link={subProductPath}
          isVisible={isVisible}
          hasAccess={isTrueOrUndefinedFn(dashboard.has_access)}
        />
      ))}
      {countryDashboards.length > 0 && (
        <SidebarProductCountries
          dashboards={countryDashboards}
          subProductCode={subProductCode}
          link={subProductPath}
          isVisible={isVisible}
          label={subProduct.name}
        />
      )}
      {customDashboards.length > 0 && (
        <SidebarProductCustomDashboards
          subProductCode={subProductCode}
          link={subProductPath}
          isVisible={isVisible}
          dashboards={customDashboards}
          groupedCountries={countryDashboards}
          label={customDashboardLabel || `Custom ${getLabelWithPluralFn(name)}`}
        />
      )}
    </>
  )
}

export default SidebarProductSubProduct
